/**
 * verifySdkPopup
 *
 * Drives the dashboard-side half of the SDK presence check: open the
 * merchant's site in a popup with ?sxi_verify=TOKEN, then resolve when
 * either (a) the popup's SDK postMessages us back, or (b) the API
 * Worker's status endpoint flips to 'verified'. Whichever lands first.
 *
 * Why two signals instead of one: postMessage gives near-instant UX
 * when the popup is open in the same browser process, but it can be
 * dropped silently if a CSP or extension blocks the message — polling
 * is the safety net. The server-side status is the source of truth.
 *
 * Returns:
 *   { ok: true, inHead: boolean, sdkVersion: string|null }      on verified
 *   { ok: false, reason: 'not_detected'|'mismatch'|'popup_blocked'|'timeout',
 *     mismatchReason?: string, inHead?: boolean }               on failure
 *
 * waitForDiagnostics (self-serve onboarding, Phase 3): the popup was opened with sxi_diag=1, so
 * after its beacon the SDK tests the page (a first load and a reload, ~15-30s) and sends the
 * results back on the check. A passing check then keeps the popup open (onVerified tells the UI)
 * until the status says the results arrived, or the popup says it is done, and resolves with
 * diagnostics: 'received' | 'failed' (the popup couldn't send them) | 'timeout' | 'closed'.
 * The test still passes without them; the recommendation then sends the page to our team.
 */

// Polling schedule: hit fast right after the popup opens (the SDK
// usually fires its beacon within 1-3s of page load), then back off so
// a stuck check doesn't hammer the API with hundreds of requests over
// the 5-minute deadline. Cap at 12s once we're past the first ~20s.
const POLL_SCHEDULE_MS = [1500, 2000, 2500, 3000, 4000, 6000, 8000, 10000];
const POLL_MAX_INTERVAL_MS = 12000;
// While the diagnostics run: poll steadily, and give up on them after this long.
const DIAGNOSTICS_POLL_MS = 2000;
const DIAGNOSTICS_TIMEOUT_MS = 90000;

function logDebug(...args) {
  if (typeof window !== 'undefined' && window.__sxiVerifyDebug !== false) {
    // eslint-disable-next-line no-console
    console.info('[sxi:verify]', ...args);
  }
}

export async function verifySdkPopup({
  popupUrl, pollUrl, token, expiresInMs,
  waitForDiagnostics = false, diagnosticsTimeoutMs = DIAGNOSTICS_TIMEOUT_MS, onVerified,
}) {
  logDebug('opening popup', { popupUrl, pollUrl, token, expiresInMs });

  // Try to open the popup first — must be inside the user-gesture handler
  const popup = window.open(
    popupUrl,
    'sxi-verify',
    'popup=yes,width=900,height=700,left=200,top=100'
  );

  if (!popup || popup.closed || typeof popup.closed === 'undefined') {
    logDebug('popup blocked');
    return { ok: false, reason: 'popup_blocked' };
  }

  const deadline = Date.now() + (expiresInMs || 300000);

  return new Promise((resolve) => {
    let settled = false;
    let pollTimer = null;
    let popupCheckTimer = null;
    let pollIndex = 0;
    let pollCount = 0;
    let verified = null;          // the passing result, while the diagnostics run
    let diagnosticsTimer = null;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (popupCheckTimer) clearInterval(popupCheckTimer);
      if (diagnosticsTimer) clearTimeout(diagnosticsTimer);
      window.removeEventListener('message', onMessage);
      try { if (popup && !popup.closed) popup.close(); } catch (_) {}
      logDebug('resolved', { result, polls: pollCount });
      resolve(result);
    };

    const passing = (data) => ({ ok: true, inHead: !!data.in_head, sdkVersion: data.sdk_version || null });

    // A verified check: done, or with waitForDiagnostics, done once its diagnostics arrive.
    const passed = (data) => {
      if (!waitForDiagnostics) {
        finish(passing(data));
        return;
      }
      if (data.diagnostics === 'received') {
        finish({ ...passing(data), diagnostics: 'received' });
        return;
      }
      if (verified) return;
      verified = passing(data);
      logDebug('verified; waiting for the diagnostics');
      try { if (onVerified) onVerified(verified); } catch (_) { /* the UI's problem, not the check's */ }
      diagnosticsTimer = setTimeout(() => finish({ ...verified, diagnostics: 'timeout' }), diagnosticsTimeoutMs);
    };

    // Single source of truth for resolving the helper: poll once and turn
    // the server's view into a result. `fallbackReason` is what we report
    // if the server has no useful info (status pending, no mismatch).
    const finalize = async (fallbackReason) => {
      let data = null;
      try {
        const r = await fetch(pollUrl, { method: 'GET', credentials: 'omit' });
        if (r.ok) data = await r.json();
      } catch (_) { /* swallow — fallthrough to fallback */ }

      if (data && data.status === 'verified') {
        if (!waitForDiagnostics) {
          finish(passing(data));
        } else {
          const diagnostics = data.diagnostics === 'received' ? 'received' : fallbackReason === 'timeout' ? 'timeout' : 'closed';
          finish({ ...passing(data), diagnostics });
        }
        return;
      }
      if (verified) {
        finish({ ...verified, diagnostics: fallbackReason === 'timeout' ? 'timeout' : 'closed' });
        return;
      }
      if (data && data.status === 'expired') {
        finish({ ok: false, reason: 'timeout' });
        return;
      }
      if (data && data.mismatch_reason) {
        finish({
          ok: false,
          reason: 'mismatch',
          mismatchReason: data.mismatch_reason,
          inHead: typeof data.in_head === 'boolean' ? data.in_head : undefined
        });
        return;
      }
      finish({ ok: false, reason: fallbackReason });
    };

    const onMessage = (evt) => {
      const data = evt && evt.data;
      if (!data || data.token !== token) return;
      if (data.type === 'sxi:diagnostics:done' && waitForDiagnostics) {
        logDebug('diagnostics done in the popup', data);
        // The popup couldn't send its results: a pass without them. Otherwise confirm with the server.
        if (data.ok === false) {
          pollOnce().then(() => { if (verified) finish({ ...verified, diagnostics: 'failed' }); });
          return;
        }
        pollOnce();
        return;
      }
      if (data.type !== 'sxi:verify:done') return;
      logDebug('postMessage from popup', data);
      // postMessage tells us the SDK fired its beacon; the server is the
      // source of truth, so do an immediate status check.
      pollOnce();
    };
    window.addEventListener('message', onMessage);

    const pollOnce = async () => {
      pollCount++;
      try {
        const r = await fetch(pollUrl, { method: 'GET', credentials: 'omit' });
        if (!r.ok) {
          logDebug('poll non-OK', { count: pollCount, status: r.status });
          return;
        }
        const data = await r.json();
        logDebug('poll', { count: pollCount, status: data.status, mismatch: data.mismatch_reason });
        if (data.status === 'verified') {
          passed(data);
          return;
        }
        if (data.status === 'expired') {
          finish(verified ? { ...verified, diagnostics: 'timeout' } : { ok: false, reason: 'timeout' });
          return;
        }
        // Pending or mismatch: keep polling. Mismatch is only terminal
        // once we've given up (deadline / popup-closed) — the user might
        // still fix the install while the popup is open.
      } catch (err) {
        logDebug('poll error', { count: pollCount, error: err && err.message });
      }
    };

    const scheduleNextPoll = () => {
      if (settled) return;
      if (Date.now() > deadline) {
        finalize('timeout');
        return;
      }
      const delay = verified ? DIAGNOSTICS_POLL_MS : (POLL_SCHEDULE_MS[pollIndex] ?? POLL_MAX_INTERVAL_MS);
      pollIndex++;
      pollTimer = setTimeout(async () => {
        if (settled) return;
        if (Date.now() > deadline) {
          finalize('timeout');
          return;
        }
        await pollOnce();
        scheduleNextPoll();
      }, delay);
    };
    scheduleNextPoll();

    popupCheckTimer = setInterval(() => {
      if (popup && popup.closed && !settled) {
        logDebug('popup closed; finalizing');
        // Give the SDK a beat to land its beacon before reading status,
        // then finalize with whatever the server has recorded.
        setTimeout(() => { if (!settled) finalize('not_detected'); }, 500);
      }
    }, 500);
  });
}

export function describeVerifyFailure(result) {
  if (!result || result.ok) return '';
  switch (result.reason) {
    case 'popup_blocked':
      return 'Popup was blocked. Please allow popups for this site and try again.';
    case 'timeout':
      return 'Verification timed out. Open your storefront in a new tab and try again.';
    case 'mismatch':
      if (result.mismatchReason === 'client_key_mismatch') {
        return 'A Cognitive UX SDK was detected on your site, but with a different client key. Please check the script tag.';
      }
      if (result.mismatchReason === 'hostname_mismatch') {
        return 'The SDK was detected on a different domain than the one you entered. Please confirm the URL.';
      }
      return 'SDK detected but configuration does not match.';
    case 'not_detected':
    default:
      return 'SDK not detected on your site. Make sure the script tag is installed in <head>, then try again.';
  }
}
